'use client';

import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/utils';
import { clearBrowserTimeout, getBrowserWindow, setBrowserTimeout } from '@/lib/utils/browser-window';
import { TweetTextWithPreviews } from './TweetTextWithPreviews';

interface ExpandableTweetTextProperties {
  className?: string;
  collapsedHtml?: string;
  /**
   * When true, the component may collapse overflowing content behind a “Show more” control.
   * Consumers should explicitly opt-in based on boundary-normalized metadata (e.g. noteTweet)
   * to avoid heuristics that guess when to truncate.
   */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  footerSlot?: ReactNode;
  // If true, show the expand control even if the text doesn't overflow (used for long-form tweets)
  forceShowButton?: boolean;
  html: string;
  maxLines?: number;
  mentionProfiles?: Map<string, { username: string; name?: string | null; profileImageUrl?: string | null }>;
  // Optional async hook invoked on first expand. Caller can fetch full text and update html upstream.
  onExpand?: () => Promise<void> | void;
  onShowMoreVisibilityChange?: (visible: boolean) => void;
}

// Increase default line threshold so “Show more” appears less often.
export function ExpandableTweetText(properties: ExpandableTweetTextProperties) {
  const { html, collapsedHtml, defaultExpanded = false } = properties;
  const remountKey = `${collapsedHtml ?? html}|${defaultExpanded ? 'expanded' : 'collapsed'}`;

  return <ExpandableTweetTextInner key={remountKey} {...properties} />;
}

function ExpandableTweetTextInner({
  html,
  collapsedHtml,
  className = '',
  maxLines = 10,
  collapsible = false,
  forceShowButton = false,
  onExpand,
  onShowMoreVisibilityChange,
  defaultExpanded = false,
  footerSlot,
  mentionProfiles,
}: ExpandableTweetTextProperties) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const contentReference = useRef<HTMLDivElement>(null);
  const expandTriggeredReference = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const allowCollapse = collapsible || forceShowButton;

  const shouldAutoExpand = defaultExpanded && !expandTriggeredReference.current;

  useEffect(() => {
    if (!shouldAutoExpand) {
      return;
    }

    expandTriggeredReference.current = true;

    if (!onExpand) {
      return;
    }

    const abortController = new AbortController();
    const run = async () => {
      try {
        setIsLoading(true);
        await onExpand();
      } catch {
        // swallow errors so default expanded state still renders
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    run().catch(() => {
      setIsLoading(false);
    });

    return () => {
      abortController.abort();
    };
  }, [onExpand, shouldAutoExpand]);

  const visibilityCallbackReference = useRef(onShowMoreVisibilityChange);
  visibilityCallbackReference.current = onShowMoreVisibilityChange;

  const previousVisibilityReference = useRef<boolean | null>(null);
  const updateVisibility = useCallback((nextVisible: boolean) => {
    if (previousVisibilityReference.current === nextVisible) {
      return;
    }
    previousVisibilityReference.current = nextVisible;
    visibilityCallbackReference.current?.(nextVisible);
  }, []);

  useEffect(() => {
    if (!allowCollapse) {
      updateVisibility(false);
      return;
    }

    if (collapsedHtml) {
      updateVisibility(true);
      return;
    }

    const element = contentReference.current;
    if (!element) {
      updateVisibility(forceShowButton);
      return;
    }

    const browserWindow = getBrowserWindow();

    const measure = () => {
      const lineHeightValue = browserWindow?.getComputedStyle(element).lineHeight ?? '24';
      const lineHeight = Number.parseFloat(lineHeightValue) || 24;
      const maxHeight = lineHeight * maxLines;
      const actualHeight = element.scrollHeight;

      // Only show expand button if content truly overflows
      const overflowed = actualHeight > maxHeight + 10; // Add small buffer for rounding
      setNeedsExpansion(overflowed);
      updateVisibility(forceShowButton || overflowed);
    };

    measure();

    if (globalThis.ResizeObserver) {
      const observer = new ResizeObserver(() => {
        measure();
      });
      observer.observe(element);
      return () => {
        observer.disconnect();
      };
    }

    const timeout = setBrowserTimeout(measure, 0);
    return () => {
      clearBrowserTimeout(timeout);
    };
  }, [allowCollapse, collapsedHtml, forceShowButton, maxLines, updateVisibility]);

  // Don't rely on character count since tweets can be longer than 280 chars now
  // Let the overflow detection handle whether to show expansion

  const shouldShowButton =
    allowCollapse && (forceShowButton || Boolean(collapsedHtml) || (collapsedHtml ? false : needsExpansion));
  const isContentExpanded = allowCollapse ? isExpanded : true;
  const useCollapsedHtml = allowCollapse && !isContentExpanded && typeof collapsedHtml === 'string';
  const applyClampStyles = !useCollapsedHtml && allowCollapse && shouldShowButton && !isContentExpanded;

  const handleExpandClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!expandTriggeredReference.current && onExpand) {
      expandTriggeredReference.current = true;
      setIsLoading(true);
      (async () => {
        try {
          await onExpand();
        } catch {
          // swallow errors; expanding still works on truncated content
        } finally {
          setIsLoading(false);
        }
      })().catch(() => {
        setIsLoading(false);
      });
    }
    setIsExpanded((current) => !current);
  };

  const renderExpandButton = () => (
    <button
      type="button"
      onClick={handleExpandClick}
      className="text-primary font-normal text-[15px] hover:underline focus:outline-none focus:underline transition-colors"
      disabled={isLoading}
      aria-expanded={isContentExpanded}
      aria-label={(() => {
        if (isContentExpanded) {
          return 'Show less tweet text';
        }
        if (isLoading) {
          return 'Loading full tweet';
        }
        return 'Show more tweet text';
      })()}
      aria-busy={isLoading}
    >
      {(() => {
        if (isContentExpanded) {
          return 'Show less';
        }
        if (isLoading) {
          return (
            <span className="inline-flex items-center gap-1">
              <svg className="h-[14px] w-[14px] animate-spin text-primary" viewBox="0 0 24 24" aria-hidden>
                <title>Loading tweet</title>
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Loading…
            </span>
          );
        }
        return 'Show more';
      })()}
    </button>
  );

  const shouldRenderFooter = shouldShowButton || Boolean(footerSlot);
  const footerClassName = cn(
    'mt-2 flex flex-wrap items-center gap-3',
    !shouldShowButton && footerSlot != null ? 'justify-end' : null
  );

  const contentStyle = useMemo<CSSProperties | null>(() => {
    if (!applyClampStyles) {
      return null;
    }
    if (isContentExpanded) {
      return { maxHeight: 'none', overflow: 'visible' } satisfies CSSProperties;
    }
    return {
      maxHeight: `${maxLines * 1.5}em`,
      overflow: 'hidden',
      maskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
    } satisfies CSSProperties;
  }, [applyClampStyles, isContentExpanded, maxLines]);

  return (
    <div className="relative">
      <div
        ref={contentReference}
        className={`relative transition-all duration-200 ${className}`}
        style={contentStyle ?? undefined}
      >
        <TweetTextWithPreviews
          html={useCollapsedHtml ? collapsedHtml : html}
          className=""
          {...(mentionProfiles ? { mentionProfiles } : {})}
        />
      </div>

      {shouldRenderFooter ? (
        <div className={footerClassName}>
          {shouldShowButton ? renderExpandButton() : null}
          {footerSlot}
        </div>
      ) : null}
    </div>
  );
}
